import {Icon} from '@udemy-v2/icons';
import {IconButton} from '@udemy-v2/react-core-components';
import classNames from 'classnames';
import React, {MouseEvent, useState} from 'react';

import {ReviewFeedbackSubmitEvent, ReviewFeedbackClearEvent} from '../../../../events/events';
import {useHelpfulness} from '../../hooks/use-helpfulness';

import styles from './review-feedback.module.scss';
import {useI18n} from '@udemy-v2/i18n';
import {publishEvent} from '@udemy-v2/event-tracking';

interface ReviewFeedbackProps {
    reviewId: number;
    frontendTrackingId?: string;
    reviewableObjectId: number;
    reviewableObjectType: string;
    isFeaturedReview?: boolean;
    reviewAuthorName: string;
}

export const ReviewFeedback = ({
    reviewId,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    isFeaturedReview = false,
    reviewAuthorName,
}: ReviewFeedbackProps) => {
    const [statusMessage, setStatusMessage] = useState('');
    const {gettext, interpolate} = useI18n();
    const {value: helpfulnessValue, onToggle: onHelpfulnessToggle} = useHelpfulness({
        reviewId,
        courseId: reviewableObjectId,
        viewPosition: 1,
        page: 1,
        filterState: {rating: null, term: null},
    });

    const handleFeedbackClick = (e: MouseEvent<HTMLButtonElement>) => {
        const {value: feedbackButtonValue} = e.currentTarget as HTMLButtonElement;
        const feedbackHelpfulness = feedbackButtonValue === 'yes';

        if (frontendTrackingId) {
            if (
                (feedbackButtonValue === 'yes' && helpfulnessValue === true) ||
                (feedbackButtonValue === 'no' && helpfulnessValue === false)
            ) {
                publishEvent(
                    new ReviewFeedbackClearEvent({
                        courseTrackingId: frontendTrackingId,
                        reviewId,
                        isFeatured: isFeaturedReview,
                        wasHelpful: feedbackHelpfulness,
                        reviewableObjectId,
                        reviewableObjectType,
                    }),
                );
                setStatusMessage(
                    interpolate(
                        gettext('Removed feedback for review by %(name)s as %(feedback)s'),
                        {
                            name: reviewAuthorName,
                            feedback: feedbackHelpfulness
                                ? gettext('Helpful')
                                : gettext('Unhelpful'),
                        },
                        true,
                    ),
                );
            } else {
                publishEvent(
                    new ReviewFeedbackSubmitEvent({
                        courseTrackingId: frontendTrackingId,
                        reviewId,
                        isFeatured: isFeaturedReview,
                        isHelpful: feedbackHelpfulness,
                        reviewableObjectId,
                        reviewableObjectType,
                    }),
                );
                setStatusMessage(
                    interpolate(
                        gettext('%(feedback)s'),
                        {
                            feedback: feedbackHelpfulness
                                ? gettext('Helpful')
                                : gettext('Unhelpful'),
                        },
                        true,
                    ),
                );
            }
        }
        onHelpfulnessToggle(feedbackHelpfulness);
    };

    const thumbupicon = helpfulnessValue === true ? 'thumb-up-filled' : 'thumb-up';
    const thumbdownicon = helpfulnessValue === false ? 'thumb-down-filled' : 'thumb-down';

    return (
        <div className={styles['feedback-container']}>
            <span className={classNames('ud-text-xs', styles['feedback-label'])}>
                {gettext('Helpful?')}
            </span>
            <div className={styles['feedback-actions']}>
                <IconButton size="small" udStyle="ghost" onClick={handleFeedbackClick} value="yes">
                    <Icon
                        glyph={thumbupicon}
                        label={
                            helpfulnessValue === true
                                ? interpolate(
                                      gettext('Undo mark review by %(name)s as helpful'),
                                      {name: reviewAuthorName},
                                      true,
                                  )
                                : interpolate(
                                      gettext('Mark review by %(name)s as helpful'),
                                      {name: reviewAuthorName},
                                      true,
                                  )
                        }
                        color="neutral"
                        size="small"
                    />
                </IconButton>
                <IconButton size="small" udStyle="ghost" onClick={handleFeedbackClick} value="no">
                    <Icon
                        glyph={thumbdownicon}
                        label={
                            helpfulnessValue === false
                                ? interpolate(
                                      gettext('Undo mark review by %(name)s as unhelpful'),
                                      {name: reviewAuthorName},
                                      true,
                                  )
                                : interpolate(
                                      gettext('Mark review by %(name)s as unhelpful'),
                                      {name: reviewAuthorName},
                                      true,
                                  )
                        }
                        color="neutral"
                        size="small"
                    />
                </IconButton>
            </div>
            <div role="status" aria-live="polite" aria-atomic="true" className="ud-sr-only">
                {statusMessage}
            </div>
        </div>
    );
};
